import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPortfolioBySlug } from '../utils/api';
import NotFoundPage from './NotFoundPage';

export default function PortfolioDetailPage() {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImgIndex, setActiveImgIndex] = useState(null);

    useEffect(() => {
        const loadProject = async () => {
            if (!slug) {
                setLoading(false);
                return;
            }
            const data = await getPortfolioBySlug(slug);
            if (data) {
                setProject({
                    title: data.title.rendered,
                    content: data.content.rendered,
                    tag: data.meta?.tag || 'PROJECT',
                    category: data.meta?.category || '',
                    client: data.meta?.client || '',
                    year: data.meta?.year || '',
                    gallery: data.meta?.gallery || [],
                    website_url: data.meta?.website_url || '',
                    image: data._embedded?.['wp:featuredmedia']?.[0]?.source_url || ''
                });
            }
            setLoading(false);
        };
        loadProject();
    }, [slug]);

    // Keyboard support for Lightbox
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (activeImgIndex === null) return;
            if (e.key === 'Escape') setActiveImgIndex(null);
            if (e.key === 'ArrowRight') handleNext();
            if (e.key === 'ArrowLeft') handlePrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [activeImgIndex]);

    const handleNext = () => {
        if (!project?.gallery) return;
        setActiveImgIndex((prev) => (prev + 1) % project.gallery.length);
    };

    const handlePrev = () => {
        if (!project?.gallery) return;
        setActiveImgIndex((prev) => (prev - 1 + project.gallery.length) % project.gallery.length);
    };

    if (loading) {
        return (
            <div className="pt-32 px-6 sm:px-8 flex items-center justify-center min-h-screen">
                <div className="w-12 h-12 border-4 border-nocturne-purple border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!project) {
        return <NotFoundPage />;
    }

    return (
        <div className="pt-16 min-h-screen">
            {/* Header Hero */}
            <section className="py-20 px-6 sm:px-8 relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none opacity-50" />
                <div className="max-w-6xl mx-auto relative z-10">
                    <Link
                        to="/portfolio"
                        className="text-nocturne-muted hover:text-nocturne-text transition-colors text-sm mb-12 flex items-center gap-2"
                    >
                        ← Back to Portfolio
                    </Link>

                    <div className="grid md:grid-cols-[1.5fr_1fr] gap-12 items-start mt-8">
                        <div>
                            <p className="section-tag mb-4">{project.tag}</p>
                            <h1 className="text-5xl md:text-7xl font-black text-nocturne-text leading-tight mb-8 uppercase">
                                {project.title}
                            </h1>
                            <p className="text-nocturne-muted text-xl leading-relaxed max-w-xl">
                                {project.category}
                            </p>
                        </div>

                        <div className="card space-y-6">
                            <div>
                                <div className="text-nocturne-purple text-xs font-bold uppercase tracking-widest mb-1">Client</div>
                                <div className="text-nocturne-text font-semibold">{project.client || 'Confidential'}</div>
                            </div>
                            <div>
                                <div className="text-nocturne-purple text-xs font-bold uppercase tracking-widest mb-1">Year</div>
                                <div className="text-nocturne-text font-semibold">{project.year || '2024'}</div>
                            </div>
                            <div>
                                <div className="text-nocturne-purple text-xs font-bold uppercase tracking-widest mb-1">Service</div>
                                <div className="text-nocturne-text font-semibold">{project.tag}</div>
                            </div>

                            {project.website_url && (
                                <div className="pt-4 border-t border-nocturne-border">
                                    <a
                                        href={project.website_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-primary flex items-center justify-center gap-2 py-3 text-sm w-full"
                                    >
                                        Visit Website <span>↗</span>
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
            {/* Main Content & Gallery */}
            <section className="py-16 px-6 sm:px-8 relative">
                <div className="max-w-6xl mx-auto">


                    {/* Project Description */}
                    <div className=" mx-auto mb-24">
                        <div className="prose prose-invert prose-purple max-w-none text-nocturne-muted leading-relaxed text-lg 
                                       [&>p]:mb-8 [&>h2]:text-nocturne-text [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-16 [&>h2]:mb-6
                                       [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-8 [&>li]:mb-2"
                            dangerouslySetInnerHTML={{ __html: project.content }}
                        />
                    </div>

                    {/* Gallery Section */}
                    {project.gallery && project.gallery.length > 0 && (
                        <div className="pt-16 border-t border-nocturne-border">
                            <h2 className="text-3xl font-black text-nocturne-text mb-12 uppercase tracking-tight">Project <span className="gradient-text">Showcase</span></h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {project.gallery.map((url, i) => (
                                    <div
                                        key={i}
                                        onClick={() => setActiveImgIndex(i)}
                                        className="group rounded-2xl overflow-hidden border border-nocturne-border bg-nocturne-card hover:border-nocturne-purple/40 transition-all shadow-lg aspect-square cursor-zoom-in relative"
                                    >
                                        <img src={url} alt={`${project.title} showcase ${i}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-nocturne-purple/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <span className="text-nocturne-text text-3xl">⊕</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Lightbox Slider */}
            {activeImgIndex !== null && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-nocturne-bg/95 backdrop-blur-xl p-4 md:p-10 animate-fade-in">
                    {/* Controls */}
                    <button
                        onClick={() => setActiveImgIndex(null)}
                        className="absolute top-6 right-6 text-nocturne-text hover:text-nocturne-text text-4xl font-light transition-colors z-[110]"
                    >
                        ✕
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                        className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-nocturne-text hover:bg-nocturne-purple hover:text-nocturne-text transition-all z-[110]"
                    >
                        ←
                    </button>

                    <button
                        onClick={(e) => { e.stopPropagation(); handleNext(); }}
                        className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full border border-white/10 bg-white/5 flex items-center justify-center text-nocturne-text hover:bg-nocturne-purple hover:text-nocturne-text transition-all z-[110]"
                    >
                        →
                    </button>

                    {/* Image Container */}
                    <div
                        className="relative max-w-7xl max-h-full flex items-center justify-center select-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={project.gallery[activeImgIndex]}
                            alt="Full screen view"
                            className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl animate-scale-up"
                        />
                        <div className="absolute bottom-[-40px] left-0 right-0 text-center text-nocturne-muted text-sm font-semibold tracking-widest">
                            IMAGE {activeImgIndex + 1} OF {project.gallery.length}
                        </div>
                    </div>
                </div>
            )}

            {/* Contact CTA */}
            <section className="py-24 px-6 sm:px-8">
                <div className="max-w-4xl mx-auto">
                    <div className="card border-nocturne-purple/30 bg-card-gradient py-16 px-8 text-center glow-purple">
                        <h2 className="text-4xl md:text-5xl font-black text-nocturne-text mb-6 uppercase tracking-tight">
                            Build Your Next <span className="gradient-text">Project</span>
                        </h2>
                        <p className="text-nocturne-muted mb-10 max-w-md mx-auto">
                            Ready to achieve similar results? Let's discuss your technical requirements and define the perfect architecture.
                        </p>
                        <Link to="/contact" className="btn-primary inline-block px-10 py-4 text-base">
                            Synthesize →
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
